import { useAllViewsLink } from '@monorepo/logs/src/hooks/useLogsLinks';
import { useProjectEnabledDatasetIds } from '@monorepo/logs/src/hooks/useProjectEnabledDatasetIds';
import { Button } from '@monorepo/shared/componentsV2/Button';
import { Loading } from '@monorepo/shared/componentsV2/Loading';
import { BaseModal } from '@monorepo/shared/componentsV2/modals/BaseModal';
import { ModalFormInstructions } from '@monorepo/shared/componentsV2/modals/contents/ModalFormInstructions';
import { useDatasets } from '@monorepo/shared/hooks/datasets/useDatasets';
import { DatasetPicker } from '@monorepo/workflowDataSetup/src/components/shared/DatasetPicker/DatasetPicker';
import { EmptyDatasetList } from '@monorepo/workflowDataSetup/src/components/shared/DatasetPicker/EmptyDatasetList';
import React, { useCallback, useMemo } from 'react';
import { Form } from 'react-final-form';
import { createSearchParams, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

interface CreateNewViewModalProps {
  closeModal: () => void;
  isOpen: boolean;
}

type FormValues = { datasetId: string };

// giving it some height so the dropdown doesn't look as weird when it gets opened
const ModalContent = styled.div`
  min-height: 15rem;
`;

function CreateViewDatasetPicker() {
  const { organizationId, projectId } = useParams();
  if (!organizationId || !projectId) {
    throw new Error(
      "CreateViewDatasetPickerSection rendered on a page that doesn't have organization or project id in the url.",
    );
  }

  const { datasets, isFetching: isDatasetsFetching } = useDatasets({
    organizationId,
  });
  const { projectDatasetIds, isFetching: isEnabledDatasetIdsFetching } =
    useProjectEnabledDatasetIds({
      organizationId,
      projectId,
    });
  const enabledDatasets = useMemo(() => {
    if (!datasets || !projectDatasetIds) return [];
    return datasets.filter((d) => projectDatasetIds.includes(d.id));
  }, [datasets, projectDatasetIds]);

  if (isDatasetsFetching || isEnabledDatasetIdsFetching) {
    return <Loading size="medium" />;
  }

  if (!enabledDatasets.length) {
    return <EmptyDatasetList />;
  }

  return (
    <DatasetPicker
      label="Data source"
      datasets={enabledDatasets}
      fieldName="datasetId"
      required
    />
  );
}

export function CreateNewViewModal({
  closeModal,
  isOpen,
}: CreateNewViewModalProps) {
  const navigate = useNavigate();
  const allViewsPageLink = useAllViewsLink();

  const onSubmit = useCallback(
    async (values: FormValues) => {
      const selectedDatasetId = values.datasetId;
      const queryParams = createSearchParams({ originId: selectedDatasetId });
      const link = `${allViewsPageLink}/new?${queryParams}`;
      navigate(link);
    },
    [allViewsPageLink, navigate],
  );

  return (
    <Form onSubmit={onSubmit} subscription={{ pristine: true }}>
      {({ handleSubmit, pristine }) => (
        <BaseModal
          buttons={[
            <Button
              variant="text"
              color="primary"
              onClick={closeModal}
              key="cancel"
            >
              Cancel
            </Button>,
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={pristine}
              key="save"
            >
              Generate Report
            </Button>,
          ]}
          onClose={closeModal}
          open={isOpen}
          showCloseButton
          title="Create new Report"
          maxWidth="md"
        >
          <ModalContent>
            <ModalFormInstructions>
              Choose the central data source for your Report. Data sources can
              be Log records, Resources, or Workflow results.
            </ModalFormInstructions>
            <CreateViewDatasetPicker />
          </ModalContent>
        </BaseModal>
      )}
    </Form>
  );
}
